import { useState } from 'react';
import { Bug, Search, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { updateTask } from '../store';
import { Project, Task, TaskStatus, User } from '../types';
import TaskDetailsModal from '../components/TaskDetailsModal';
import SurfaceCard from '../components/SurfaceCard';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { getProjectTeamUsers } from '../utils/projectAccess';
import { getTaskOrder, sortByTaskOrder } from '../utils/taskFiltering';
import { APP_FILTER_LABEL_CLASS, APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import { getPriorityColors, getStatusColors } from '../constants/colorScheme';

export default function BugTracker() {
  const { state, loading, reload } = useApp();
  const { projects, tasks, users, sprints } = useOrganizationData(state, {
    includeAllOrganizations: true,
  }) as {
    projects: Project[];
    tasks: Task[];
    users: User[];
    sprints: any[];
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'All'>('All');
  const [filterPriority, setFilterPriority] = useState<'All' | 'Low' | 'Medium' | 'High' | 'Critical'>('All');
  const [showCustomerVisible, setShowCustomerVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  if (loading || !state) {
    return <div className="p-8 text-center">Loading bugs...</div>;
  }

  if (!state.currentUser) {
    return <div className="p-8 text-center">Loading user context...</div>;
  }

  const refreshState = async () => {
    const email = state.currentUser?.email;
    if (email) {
      await reload(email);
    }
  };

  const bugs = tasks.filter((t) => t.type === 'Bug');
  const filteredBugs = bugs?.filter((bug) => {
    const matchesSearch = bug.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         bug.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (bug.reportedBy && bug.reportedBy.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'All' || bug.status === filterStatus;
    const matchesPriority = filterPriority === 'All' || bug.priority === filterPriority;
    const matchesCustomerVisible = !showCustomerVisible || bug.customerVisible;
    return matchesSearch && matchesStatus && matchesPriority && matchesCustomerVisible;
  });

  const orderedFilteredBugs = sortByTaskOrder(filteredBugs);

  const bugStats = {
    total: bugs.length,
    open: bugs.filter((b) => b.status !== 'Completed' && b.status !== 'Not Required').length,
    critical: bugs.filter((b) => b.priority === 'Critical' && b.status !== 'Completed').length,
    customerReported: bugs.filter((b) => b.reportedBy).length,
  };

  const selectedProject: Project | null = selectedTask
    ? projects.find((project) => project.id === selectedTask.projectId) || null
    : null;
  const selectedProjectUsers = selectedProject
    ? getProjectTeamUsers(selectedProject, users, state?.teams || [])
    : [];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="mb-2 text-3xl text-foreground">Bug Tracker</h1>
        <p className="text-muted-foreground">Track and manage bugs across all projects</p>
      </div>

      {/* Bug Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SurfaceCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <Bug className="h-8 w-8 text-muted-foreground" />
            <span className="text-3xl text-foreground">{bugStats.total}</span>
          </div>
          <h3 className="text-muted-foreground">Total Bugs</h3>
        </SurfaceCard>

        <SurfaceCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <Clock className="h-8 w-8 text-primary" />
            <span className="text-3xl text-foreground">{bugStats.open}</span>
          </div>
          <h3 className="text-muted-foreground">Open Bugs</h3>
        </SurfaceCard>

        <SurfaceCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <span className="text-3xl text-foreground">{bugStats.critical}</span>
          </div>
          <h3 className="text-muted-foreground">Critical</h3>
        </SurfaceCard>

        <SurfaceCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="h-8 w-8 text-primary" />
            <span className="text-3xl text-foreground">{bugStats.customerReported}</span>
          </div>
          <h3 className="text-muted-foreground">Customer Reported</h3>
        </SurfaceCard>
      </div>

      {/* Filters */}
      <SurfaceCard className="mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Search</span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search bugs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS} pl-10 pr-4`}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Status</span>
            <select
              title="Filter bugs by status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as TaskStatus | 'All')}
              className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Deferred">Deferred</option>
              <option value="Not Required">Not Required</option>
              <option value="Blocked">Blocked</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Priority</span>
            <select
              title="Filter bugs by priority"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value as any)}
              className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
            >
              <option value="All">All Priority</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Visibility</span>
            <label className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 shadow-sm hover:bg-muted/50">
              <input
                type="checkbox"
                checked={showCustomerVisible}
                onChange={(e) => setShowCustomerVisible(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm text-foreground">Customer Visible Only</span>
            </label>
          </div>
        </div>
      </SurfaceCard>

      {/* Bugs List */}
      {orderedFilteredBugs.length === 0 ? (
        <SurfaceCard className="p-12 text-center">
          <Bug className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
          <p className="mb-4 text-muted-foreground">
            {bugs.length === 0 ? 'No bugs reported yet' : 'No bugs match your filters'}
          </p>
        </SurfaceCard>
      ) : (
        <SurfaceCard className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-border bg-muted/40">
                <tr>
                  <th className="px-6 py-3 text-left text-sm text-muted-foreground">Bug</th>
                  <th className="px-6 py-3 text-left text-sm text-muted-foreground">Order</th>
                  <th className="px-6 py-3 text-left text-sm text-muted-foreground">Priority</th>
                  <th className="px-6 py-3 text-left text-sm text-muted-foreground">Status</th>
                  <th className="px-6 py-3 text-left text-sm text-muted-foreground">Reported By</th>
                  <th className="px-6 py-3 text-left text-sm text-muted-foreground">Project</th>
                  <th className="px-6 py-3 text-left text-sm text-muted-foreground">Customer Visible</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orderedFilteredBugs.map((bug) => {
                  const project = projects.find((p) => p.id === bug.projectId);
                  const bugOrder = getTaskOrder(bug);
                  return (
                    <tr
                      key={bug.id}
                      onClick={() => setSelectedTask(bug)}
                      className="cursor-pointer hover:bg-muted/40"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="mb-1 text-foreground">{bug.title}</p>
                          {bug.description && (
                            <p className="line-clamp-1 text-sm text-muted-foreground">{bug.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {bugOrder ?? '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`capitalize ${getPriorityColors(bug.priority).text}`}>
                          {bug.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`rounded-full border px-3 py-1 text-sm ${getStatusColors(bug.status).badge}`}>
                          {bug.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {bug.reportedBy || <span className="text-muted-foreground">Internal</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {project?.name || <span className="text-muted-foreground">Unknown</span>}
                      </td>
                      <td className="px-6 py-4">
                        {bug.customerVisible ? (
                          <CheckCircle className="h-5 w-5 text-primary" />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SurfaceCard>
      )}

      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={async (taskId, updates) => {
            await updateTask(taskId, updates);
            await refreshState();
            if (selectedTask.id === taskId) {
              setSelectedTask({ ...selectedTask, ...updates });
            }
          }}
          users={selectedProject ? selectedProjectUsers : users}
          currentUser={state.currentUser}
          existingTasks={tasks.filter(t => t.projectId === selectedTask.projectId)}
          sprints={sprints.filter(s => s.projectId === selectedTask.projectId)}
          teams={state?.teams || []}
          project={selectedProject}
        />
      )}
    </div>
  );
}